import { useCallback, useEffect, useMemo, useState } from 'react';
import { WHITE_LABEL_ACCENT_COLOR } from '../../../lib/white-label';
import { arrayRemove } from '../../../lib/utils';
import { XIcon } from '@heroicons/react/solid';
import AddDiscount from '../AddDiscountOption';

type Discount = {
  dayCount: number;
  percent: number;
  id?: string;
};
export type Discounts = Discount[];

const DiscountRow = ({
  index,
  setDiscounts,
  discount,
  isConfirmState,
  removeDiscount,
  priceOptions,
}) => {
  const discountedPrice = useMemo(() => {
    if (!priceOptions.price) return;
    return priceOptions.price - priceOptions.price * (discount.percent * 0.01);
  }, [priceOptions, discount]);

  const discountPriceError = useMemo(() => {
    const getMinPrice = (days: number) => (days >= 15 ? 2.5 : 5);

    if (discountedPrice < getMinPrice(discount.dayCount)) {
      return `Price after discount must be at least $${getMinPrice(
        discount.dayCount,
      ).toFixed(2)} for 15 days or ${
        discount.dayCount >= 15 ? 'more' : 'less'
      }`;
    }

    return null;
  }, [priceOptions, discount, discountedPrice]);

  const discountError = useMemo(() => {
    const { rentalLength } = priceOptions;
    if (rentalLength < discount.dayCount) {
      return 'Discount would not apply to current rental length';
    }

    if (discount.dayCount < 2) {
      return 'Discount only available for 2 or more days';
    }
  }, [discount, priceOptions]);

  const updateDiscount = useCallback(
    (key, value) => {
      setDiscounts((discounts) => {
        const updatedDiscounts = [...discounts.map((d) => ({ ...d }))];
        updatedDiscounts[index][key] = value;
        return updatedDiscounts;
      });
    },
    [index, setDiscounts],
  );
  return (
    <>
      <div
        className={`rounded-md text-gray-700 flex w-full focus:z-10 items-center py-2 bg-white text-sm font-medium hover:bg-gray-50 focus:border-2 focus:ring-opacity-50 focus:outline-none focus:ring-4`}
      >
        <div className="flex flex-col items-center w-full">
          <div className="flex items-center space-x-2 w-full">
            <div>
              <select
                className={`rounded-md text-sm  ${
                  discountError ? 'border-red-500 border ring-red-500' : ''
                }`}
                value={discount.dayCount}
                disabled={isConfirmState}
                onChange={(e) => {
                  updateDiscount('dayCount', Number(e.currentTarget.value));
                }}
              >
                {Array(priceOptions.rentalLength)
                  .fill(0)
                  .map((_, i) => (
                    <option value={i + 1}>
                      {i + 1} day{i + 1 > 1 ? 's' : ''}
                    </option>
                  ))}
              </select>
              <span className="pl-2 pr-1">at a</span>
            </div>
            <div>
              <select
                className={`rounded-md text-sm  ${
                  discountError || discountPriceError
                    ? 'border-red-500 border ring-red-500'
                    : ''
                }`}
                value={discount.percent}
                disabled={isConfirmState}
                onChange={(e) => {
                  updateDiscount('percent', Number(e.currentTarget.value));
                }}
              >
                {Array(10)
                  .fill(0)
                  .map((_, i) => (
                    <option value={(i + 1) * 5}>{(i + 1) * 5} %</option>
                  ))}
              </select>
              <span className="pl-2 pr-1">
                discount
                {discountedPrice && (
                  <>
                    {' '}
                    for{' '}
                    <span
                      className={`${discountPriceError ? 'text-red-500' : ''}`}
                    >
                      ${discountedPrice.toFixed(2)}
                    </span>{' '}
                    / day
                  </>
                )}
              </span>
              <span className="pl-2 pr-1"></span>
            </div>
          </div>
          {(discountError || discountPriceError) && (
            <div className="w-full pt-2" id="discountError">
              <p className="text-xs text-red-500">
                {discountError || discountPriceError}
              </p>
            </div>
          )}
        </div>
        <div>
          {!isConfirmState && (
            <XIcon
              onClick={() => removeDiscount(discount)}
              className="cursor-pointer w-4 h-4 text-gray-400"
            />
          )}
        </div>
      </div>
    </>
  );
};

const DiscountsSections = ({
  isConfirmState,
  discounts,
  setDiscounts,
  priceOptions,
}: {
  discounts: Discounts;
  setDiscounts: any;
  priceOptions: any;
  isConfirmState: boolean;
}) => {
  const [showAddDiscount, setShowAddDiscount] = useState(false);

  useEffect(() => {
    const newDiscounts: Discounts = [];

    if (priceOptions.rentalLength > 1) {
      const initialDayCount =
        priceOptions.rentalLength > 3
          ? Math.ceil(priceOptions.rentalLength / 2)
          : 2;

      newDiscounts.push({
        id: 'initial',
        percent: 10,
        ...(discounts.find(
          (discount) =>
            discount.id === 'initial' || discount.dayCount === initialDayCount,
        ) || {}),
        dayCount: initialDayCount,
      });

      newDiscounts.push(
        ...discounts.filter(
          (discount) =>
            discount.id !== 'initial' && discount.dayCount !== initialDayCount,
        ),
      );
    }

    setDiscounts(newDiscounts);
  }, [priceOptions]);

  const sortedDiscounts = useMemo(() => {
    return discounts?.sort((a, b) => {
      return a.dayCount - b.dayCount;
    });
  }, [discounts]);

  const removeDiscount = (discount) => {
    const a = arrayRemove(discounts, discount);
    setDiscounts(a);
  };

  function saveDiscount(dayCount: number, percent: number): void {
    const newDiscount = {
      dayCount,
      percent,
    };

    const existingDiscountIdx = discounts.findIndex(
      (discount) => discount.dayCount === dayCount,
    );
    if (existingDiscountIdx !== -1) {
      // handle updated discount
      discounts[existingDiscountIdx] = newDiscount;
      setDiscounts([...discounts]);
    } else {
      // handle new discount
      setDiscounts([...discounts, newDiscount]);
    }

    setShowAddDiscount(false);
  }

  const canAdd = useMemo(() => {
    return !discounts.find((discount) => discount.dayCount === 1);
  }, [discounts]);

  const addDiscount = useCallback(() => {
    if (canAdd) {
      setDiscounts([
        ...discounts,
        {
          dayCount: 1,
          percent: 10,
          id: discounts.length === 0 ? 'initial' : null,
        },
      ]);
    }
  }, [canAdd, discounts]);

  return (
    <div className="pb-4">
      <div className="text-lg font-medium text-gray-600 mb-4 pb-2 text-left border-b-2 border-gray-100 w-full">
        Discounts
      </div>
      <div className="" data-tour="step-7">
        {discounts.length > 0 && (
          <label className="text-gray-700 block text-sm font-medium mt-4 mb-2">
            Discount Rules
          </label>
        )}

        {sortedDiscounts.map((discount, i) => (
          <DiscountRow
            index={i}
            setDiscounts={setDiscounts}
            discount={discount}
            removeDiscount={removeDiscount}
            isConfirmState={isConfirmState}
            priceOptions={priceOptions}
          />
        ))}

        <div className="mt-3">
          {showAddDiscount ? (
            <AddDiscount
              saveDiscount={saveDiscount}
              showAdd={setShowAddDiscount}
              basePrice={priceOptions?.price}
              rentalLength={priceOptions?.rentalLength}
              discounts={discounts}
            />
          ) : (
            !isConfirmState &&
            canAdd && (
              <mark
                onClick={addDiscount}
                className="text-blue-500 font-medium cursor-pointer text-sm bg-transparent"
                style={{
                  color: `${WHITE_LABEL_ACCENT_COLOR}`,
                }}
              >
                Add another discount
              </mark>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default DiscountsSections;
